import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  LightBulbIcon,
  PlusIcon,
  TicketIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { DataGrid, GridColumn } from 'components/DataGrid'
import { LayoutLoading } from 'components/LayoutLoading'
import { Payee, PayeeCombo } from 'components/PayeeCombo'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  approvePaymentVoucher,
  createConstructionVoucher,
  createPaymentVoucher,
  downloadUnpaidVouchers,
  getConstructionVoucherSuggestions,
  getLoanPayees,
  getPaymentVouchers,
  rejectPaymentVoucher,
  voidPaymentVoucher,
} from 'services'
import { Button, Input2, Modal, Select2 } from 'stories/components'
import { confirm, formatDate, prompt, removeComma, thousandSeperatorWithPrefix } from 'utils'

import { usePayoffGuard } from '../payoffGuard'

interface Voucher {
  id: number
  voucherNumber: string
  payeeType: string
  payeeName: string
  amount: number
  memo: string
  ledgerCategory: string
  dueDate: string
  voucherType: string
  status: string
  loanPayableId: number | null
  approvedBy: string
  approvedDate: string
  rejectedReason: string
  createdBy: string
  createdAt: string
}

interface Suggestion {
  sourceType: string
  sourceId: number
  payeeType: string
  payeeName: string
  amount: number
  memo: string
  ledgerCategory: string
  voucherType: string
  label: string
}

const STATUS_STYLE: Record<string, string> = {
  Draft: 'bg-gray-100 text-gray-600',
  Pending: 'bg-amber-100 text-amber-700',
  Approved: 'bg-sky-100 text-sky-700',
  Paid: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Void: 'bg-gray-100 text-gray-400 line-through',
}

// See PartyPayments — these strings become the trust-ledger subType, so keep them exact.
const LEDGER_CATEGORIES: Record<string, string> = {
  'Construction Draw': 'Construction Draw',
  Refund: 'Refund',
  Inspection: 'Inspection',
  Appraisal: 'Appraisal',
  'Property Taxes': 'Property Taxes',
  Insurance: 'Insurance',
  Utilities: 'Utilities',
  'Repairs & Maintenance': 'Repairs & Maintenance',
  'Property Management': 'Property Management',
  Legal: 'Legal',
  Payoff: 'Payoff',
  Other: 'Other',
}

const VOUCHER_TYPES: Record<string, string> = { General: 'General', Construction: 'Construction' }

// A voucher awaiting approval can be selected for the bulk "Approve Selected" action.
const isApprovable = (v: Voucher) => v.status === 'Draft' || v.status === 'Pending'

/**
 * Payment vouchers: authorize paying any party against this loan. A voucher owns the
 * draft→pending→approved lifecycle; approving one creates a payable that enters the servicing
 * check queue, where it is paid (by check or ACH) alongside everything else going out. Once paid,
 * the voucher reconciles to Paid. Construction vouchers are the same flow, sourced from issued draws.
 *
 * Distinct from the escrow "Vouchers" tab, which schedules taxes/insurance for escrow analysis.
 */
export default function PaymentVouchers() {
  // Warns before raising a voucher against an active payoff.
  const confirmPayoff = usePayoffGuard()
  const [loading, setLoading] = useState(false)
  const [vouchers, setVouchers] = useState<Voucher[]>([])
  const [payees, setPayees] = useState<Payee[]>([])
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [selected, setSelected] = useState<number[]>([])
  const [addOpen, setAddOpen] = useState(false)
  const [prefill, setPrefill] = useState<Partial<Suggestion> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [list, who, sugg]: any[] = await Promise.all([
        getPaymentVouchers(),
        getLoanPayees(),
        getConstructionVoucherSuggestions(),
      ])
      setVouchers(Array.isArray(list?.items) ? list.items : [])
      setPayees(Array.isArray(who?.payees) ? who.payees : [])
      setSuggestions(Array.isArray(sugg?.suggestions) ? sugg.suggestions : [])
      setSelected([])
    } catch {
      toast('Could not load vouchers for this loan.', { type: 'error' })
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const approvable = useMemo(() => vouchers.filter(isApprovable), [vouchers])
  const unpaidTotal = useMemo(
    () => vouchers.filter((v) => ['Draft', 'Pending', 'Approved'].includes(v.status)).reduce((s, v) => s + (Number(v.amount) || 0), 0),
    [vouchers],
  )
  const selectedTotal = useMemo(
    () => approvable.filter((v) => selected.includes(v.id)).reduce((s, v) => s + (Number(v.amount) || 0), 0),
    [approvable, selected],
  )

  const toggleRow = (v: Voucher) => {
    if (!isApprovable(v)) return
    setSelected((cur) => (cur.includes(v.id) ? cur.filter((id) => id !== v.id) : [...cur, v.id]))
  }
  const allSelected = approvable.length > 0 && selected.length === approvable.length
  const toggleAll = () => setSelected(allSelected ? [] : approvable.map((v) => v.id))

  const onApproveSelected = async () => {
    const ids = approvable.filter((v) => selected.includes(v.id)).map((v) => v.id)
    if (!ids.length) return
    const message = (
      <div className="text-gray-600 mb-4 text-left">
        Approve {ids.length} voucher{ids.length === 1 ? '' : 's'} totalling{' '}
        <span className="font-semibold">{thousandSeperatorWithPrefix(selectedTotal)}</span>?
        <p className="text-sm mt-2">Each becomes a payment in the servicing check queue, ready to pay by check or ACH.</p>
      </div>
    )
    if (!(await confirm(message))) return
    setLoading(true)
    let ok = 0
    for (const id of ids) {
      try {
        const res: any = await approvePaymentVoucher(id)
        if (res?.success) ok++
      } catch {
        /* keep going; report the count at the end */
      }
    }
    setLoading(false)
    toast(`${ok} of ${ids.length} voucher${ids.length === 1 ? '' : 's'} approved and added to the check queue.`, {
      type: ok === ids.length ? 'success' : 'warning',
    })
    load()
  }

  const onApprove = async (v: Voucher) => {
    const res: any = await approvePaymentVoucher(v.id)
    if (res?.success) {
      toast(`Voucher ${v.voucherNumber} approved and added to the check queue.`, { type: 'success' })
      load()
    } else toast(res?.message || 'Could not approve this voucher.', { type: 'error' })
  }

  const onReject = async (v: Voucher) => {
    // prompt() resolves to the entered string, or false when cancelled (typed as string, so read loosely).
    const reason: any = await prompt(`Reject voucher ${v.voucherNumber}? Optionally note why:`)
    if (reason === false || reason == null) return
    const res: any = await rejectPaymentVoucher(v.id, typeof reason === 'string' ? reason : '')
    if (res?.success) {
      toast(`Voucher ${v.voucherNumber} rejected.`, { type: 'success' })
      load()
    } else toast(res?.message || 'Could not reject this voucher.', { type: 'error' })
  }

  const onVoid = async (v: Voucher) => {
    const message = (
      <div className="text-gray-600 mb-4 text-left">
        Void voucher <span className="font-semibold">{v.voucherNumber}</span> for{' '}
        {thousandSeperatorWithPrefix(v.amount)} to {v.payeeName}?
        <p className="text-sm mt-2">If it was approved, its queued payment is removed from the check queue too.</p>
      </div>
    )
    if (!(await confirm(message))) return
    const res: any = await voidPaymentVoucher(v.id)
    if (res?.success) {
      toast(`Voucher ${v.voucherNumber} voided.`, { type: 'success' })
      load()
    } else toast(res?.message || 'Could not void this voucher.', { type: 'error' })
  }

  const onDownload = async (format: 'pdf' | 'xls' | 'csv') => {
    try {
      await downloadUnpaidVouchers(format)
    } catch {
      toast('Could not download the report.', { type: 'error' })
    }
  }

  const columns: GridColumn<Voucher>[] = [
    { key: 'voucherNumber', label: 'Voucher #', minWidth: 90 },
    { key: 'payeeName', label: 'Payee', minWidth: 160, render: (v) => <span className="font-medium">{v.payeeName}</span> },
    { key: 'payeeType', label: 'Payee Type', minWidth: 90 },
    { key: 'voucherType', label: 'Kind', minWidth: 90 },
    { key: 'ledgerCategory', label: 'Category', minWidth: 110, render: (v) => v.ledgerCategory || '' },
    { key: 'dueDate', label: 'Due', minWidth: 90, render: (v) => (v.dueDate ? formatDate(v.dueDate) : '') },
    {
      key: 'amount',
      total: true,
      label: 'Amount',
      align: 'right',
      minWidth: 100,
      render: (v) => <span className="font-semibold">{thousandSeperatorWithPrefix(v.amount)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      minWidth: 90,
      render: (v) => (
        <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLE[v.status] || 'bg-gray-100'}`}>{v.status}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Action',
      minWidth: 150,
      sortable: false,
      render: (v) => (
        <div className="flex items-center gap-2 text-xs">
          {isApprovable(v) && (
            <>
              <button className="text-green-600 inline-flex items-center gap-0.5" onClick={() => onApprove(v)}>
                <CheckCircleIcon className="w-4 h-4" /> Approve
              </button>
              <button className="text-amber-600 inline-flex items-center gap-0.5" onClick={() => onReject(v)}>
                <XCircleIcon className="w-4 h-4" /> Reject
              </button>
            </>
          )}
          {v.status !== 'Paid' && v.status !== 'Void' && v.status !== 'Rejected' && (
            <button className="text-red-600 inline-flex items-center gap-0.5" onClick={() => onVoid(v)}>
              <TrashIcon className="w-4 h-4" /> Void
            </button>
          )}
          {v.status === 'Approved' && <span className="text-gray-400">in check queue</span>}
        </div>
      ),
    },
  ]

  return (
    <div className="payment-vouchers-container relative">
      <LayoutLoading show={loading} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <TicketIcon className="w-6 h-6 text-shade-blue" />
            Payment Vouchers
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Authorize paying a party against this loan. Approving a voucher sends it to the servicing check queue to be
            paid by check or ACH; it reconciles to Paid once the check run clears.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Unpaid vouchers</div>
          <div className="text-lg font-bold">{thousandSeperatorWithPrefix(unpaidTotal)}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button
          onClick={async () => {
            if (!(await confirmPayoff())) return
            setPrefill(null)
            setAddOpen(true)
          }}
          className="!py-1.5"
        >
          <span className="flex items-center gap-1">
            <PlusIcon className="w-4 h-4" /> New Voucher
          </span>
        </Button>
        <Button color="gray" onClick={onApproveSelected} disabled={!selected.length} className="!py-1.5">
          <span className="flex items-center gap-1">
            <CheckCircleIcon className="w-4 h-4" /> Approve Selected
            {selected.length ? ` (${selected.length} · ${thousandSeperatorWithPrefix(selectedTotal)})` : ''}
          </span>
        </Button>
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-xs text-gray-500 mr-1">Unpaid report:</span>
          <Button color="gray" onClick={() => onDownload('pdf')} className="!py-1.5">
            <span className="flex items-center gap-1">
              <DocumentArrowDownIcon className="w-4 h-4" /> PDF
            </span>
          </Button>
          <Button color="gray" onClick={() => onDownload('xls')} className="!py-1.5">
            Excel
          </Button>
          <Button color="gray" onClick={() => onDownload('csv')} className="!py-1.5">
            CSV
          </Button>
        </div>
      </div>

      {/* Issued construction draws not yet turned into a voucher — one click rather than rekeying. */}
      {suggestions.length > 0 && (
        <div className="mb-5 rounded border border-shade-blue/30 bg-shade-blue/5 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-shade-blue mb-2 flex items-center gap-1">
            <LightBulbIcon className="w-4 h-4" /> Construction draws ready to pay
          </p>
          <div className="flex flex-col gap-1.5">
            {suggestions.map((s) => (
              <div key={`${s.sourceType}-${s.sourceId}`} className="flex items-center justify-between gap-3 text-sm">
                <span>{s.label}</span>
                <button
                  type="button"
                  className="text-shade-blue hover:underline whitespace-nowrap"
                  onClick={async () => {
                    if (!(await confirmPayoff())) return
                    setPrefill(s)
                    setAddOpen(true)
                  }}
                >
                  Create voucher
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <DataGrid
        gridKey="payment-vouchers"
        columns={columns}
        rows={vouchers}
        rowKey={(v) => v.id}
        loading={loading}
        emptyText="No vouchers raised on this loan yet."
        selectable
        isRowSelected={(v) => selected.includes(v.id)}
        onToggleRow={toggleRow}
        allSelected={allSelected}
        onToggleAll={toggleAll}
      />

      <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
        Approved vouchers are paid from the{' '}
        <Link to="/payment-checks" className="text-shade-blue hover:underline inline-flex items-center gap-0.5">
          servicing check queue <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
        , where you can pay all or some by check or ACH.
      </p>

      {addOpen && (
        <AddVoucherModal
          payees={payees}
          prefill={prefill}
          onClose={() => setAddOpen(false)}
          onDone={() => {
            setAddOpen(false)
            load()
          }}
        />
      )}
    </div>
  )
}

/** Raise a voucher to pay a party. */
const AddVoucherModal = ({
  payees,
  prefill,
  onClose,
  onDone,
}: {
  payees: Payee[]
  prefill: Partial<Suggestion> | null
  onClose: () => void
  onDone: () => void
}) => {
  // A prefilled construction suggestion may name someone already on the loan; match them so the
  // voucher keeps their payee type and ACH/banking linkage rather than posting as free text.
  const prefillPayee = useMemo(
    () => (prefill ? payees.find((p) => p.payeeType === prefill.payeeType && p.name === prefill.payeeName) ?? null : null),
    [prefill, payees],
  )

  const [payeeName, setPayeeName] = useState(prefill?.payeeName || '')
  // The known payee behind the typed name, set when one is picked from the suggestions. Null means
  // free text (someone not on the loan), which posts as payeeType 'Other'. Typing clears it.
  const [selectedPayee, setSelectedPayee] = useState<Payee | null>(prefillPayee)
  const [amount, setAmount] = useState(prefill?.amount ? String(prefill.amount) : '')
  const [memo, setMemo] = useState(prefill?.memo || '')
  const [category, setCategory] = useState(prefill?.ledgerCategory || '')
  const [dueDate, setDueDate] = useState('')
  const [voucherType, setVoucherType] = useState(prefill?.voucherType || 'General')
  const [saving, setSaving] = useState(false)

  const categoryOptions = useMemo(() => {
    if (!category || LEDGER_CATEGORIES[category]) return LEDGER_CATEGORIES
    return { ...LEDGER_CATEGORIES, [category]: category }
  }, [category])

  const name = payeeName.trim()
  const numericAmount = Number(removeComma(String(amount))) || 0
  const disabled = saving || !name || !(numericAmount > 0)

  const onSubmit = async () => {
    setSaving(true)
    try {
      const payload = {
        payeeType: selectedPayee?.payeeType || 'Other',
        payeeRefId: selectedPayee?.payeeRefId ?? null,
        payeeName: name,
        payeeAddress: selectedPayee?.address || '',
        amount: numericAmount,
        memo,
        ledgerCategory: category,
        dueDate,
        voucherType,
        sourceType: prefill?.sourceType || 'manual',
        sourceId: prefill?.sourceId,
      }
      const res: any =
        voucherType === 'Construction' ? await createConstructionVoucher(payload) : await createPaymentVoucher(payload)
      if (!res?.success) {
        toast(res?.message || 'Could not create this voucher.', { type: 'error' })
        setSaving(false)
        return
      }
      toast(`Voucher ${res.voucherNumber || ''} created for ${name}.`, { type: 'success' })
      onDone()
    } catch {
      toast('Could not create this voucher.', { type: 'error' })
      setSaving(false)
    }
  }

  return (
    <Modal title="New Voucher" isOpen onClose={onClose} onOk={onSubmit} loading={saving} disabled={disabled} titleOkay="Create Voucher">
      <div className="w-[34rem] max-w-full flex flex-col gap-3">
        <Select2
          id="voucher-kind"
          title="Voucher type"
          options={VOUCHER_TYPES}
          value={voucherType}
          onChange={(v: string) => setVoucherType(v)}
        />
        <PayeeCombo
          payees={payees}
          value={payeeName}
          onType={(v) => {
            setPayeeName(v)
            setSelectedPayee(null)
          }}
          onPick={(p) => {
            setPayeeName(p.name)
            setSelectedPayee(p)
          }}
        />
        {selectedPayee && !selectedPayee.achReady && (
          <p className="text-xs text-amber-700 -mt-1">
            {selectedPayee.achBlockedReason} — this one can still be paid by check.
          </p>
        )}
        <Input2 title="Amount" type="thousandSep" prefix="$" value={amount} onChange={(v: string) => setAmount(v)} />
        <Input2
          title="Reason"
          value={memo}
          onChange={(v: string) => setMemo(v)}
          placeholder="e.g. Draw 3 - framing, vendor invoice #1234"
        />
        <div className="grid grid-cols-2 gap-3">
          <Select2
            id="voucher-category"
            title="Trust ledger category"
            options={categoryOptions}
            value={category}
            onChange={(v: any) => setCategory(v)}
            hasDefaultOption
          />
          <Input2 title="Due date" type="date" value={dueDate} onChange={(v: string) => setDueDate(v)} />
        </div>
        <p className="text-xs text-gray-500">
          Creating a voucher does not pay anyone. Approve it to send the payment to the servicing check queue; nothing
          posts to the trust ledger until it is actually paid there.
        </p>
      </div>
    </Modal>
  )
}

